import React, { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';
import { Control } from '@vlabs/uikit';
import type { TOption } from '@vlabs/uikit/types';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import st from './EditFaceInfoForm.module.sass';
import { FaceFields } from './FaceFields';
import { LinkedToCardFaceFields } from './LinkedToCardFaceFields';
import type { PatchFaceMutationBody } from '@vlabs/api/luna-api/client';
import {
  useGetFace, usePatchFace, linkFaces, getGetFaceQueryOptions,
} from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model';
import { ChangeFacePhoto } from '../../change-face-photo';

interface EditFaceInfoFormProps {
  faceId: FaceId;
  onCancel: VoidFunction;
  onSubmit?: VoidFunction;
}

interface TEditFaceInfoFormValues extends Pick<PatchFaceMutationBody, 'user_data'> {
  lists: TOption[];
}

export function EditFaceInfoForm({
  faceId,
  onCancel,
  onSubmit = undefined,
}: EditFaceInfoFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const listOptions = useSelector(selectListOptions);
  const queryClient = useQueryClient();
  const getFace = useGetFace(faceId);
  const patchFace = usePatchFace();
  const [changePhotoCallback, setChangePhotoCallback] = useState<() => Promise<void>>();

  const faceData = getFace.data;
  const hasExternalId = Boolean(faceData?.external_id);
  const formValues = useMemo<TEditFaceInfoFormValues>(() => ({
    user_data: faceData?.user_data ?? '',
    lists: (faceData?.lists ?? []).map((listId) => listOptions.find(({ value }) => value === listId)).filter(Boolean),
  }), [faceData, listOptions]);

  const form = useForm<TEditFaceInfoFormValues>({
    defaultValues: {
      user_data: '',
      lists: [],
    },
  });

  const { reset, handleSubmit } = form;

  useEffect(() => {
    if (!faceData) return;
    reset(formValues);
  }, [faceData, formValues, reset]);

  const submitHandler: SubmitHandler<TEditFaceInfoFormValues> = async ({ lists, ...data }) => {
    const currentListIds = lists.map(({ value }) => value);
    const initialListIds = faceData?.lists ?? [];

    const listsToAttach = currentListIds.filter((listId) => !initialListIds.includes(listId));
    const listsToDetach = initialListIds.filter((listId) => !currentListIds.includes(listId));

    const promises = [
      patchFace.mutateAsync({ faceId, data }),
      ...listsToAttach.map(async (listId) => linkFaces(listId, {
        action: 'attach',
        face_ids: [faceId],
      })),
      ...listsToDetach.map(async (listId) => linkFaces(listId, {
        action: 'detach',
        face_ids: [faceId],
      })),
    ];
    if (changePhotoCallback) promises.push(changePhotoCallback());
    // FIXME: здесь Promise.all и при ошибке не пользак не понимает, какие запросы успели выполниться
    // возможно, стоит перейти на Promise.allSettled
    await Promise.all(promises);

    await queryClient.invalidateQueries(getGetFaceQueryOptions(faceId));
    toast.success(t('face-page:edit_face_info.success_toast'));
    onSubmit?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className={st.Root}>
          <h5>{t('face-page:edit_face_info.title')}</h5>

          <div className={st.Content}>
            <div className={st.PreviewSection}>
              <ChangeFacePhoto
                faceId={faceId}
                onChangeFacePhoto={(sideEffect) => setChangePhotoCallback(() => sideEffect)}
              />
            </div>

            {!hasExternalId && <FaceFields />}
            {!!hasExternalId && <LinkedToCardFaceFields />}
          </div>

          <div className={st.Divider} />

          <div className={st.Footer}>
            <Control.Button
              data-testid="submitFaceEditButton"
              type="submit"
            >
              {t('face-page:common.save')}
            </Control.Button>

            <Control.Button
              data-testid="closeFaceEditButton"
              kind="secondary"
              onClick={onCancel}
              type="button"
              variant="outlined"
            >
              {t('face-page:common.cancel')}
            </Control.Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
